// import { captureException } from '@sentry/react';
import { debounce, throttle } from 'lodash';
import { createProjectBackup, updateProjectBackup } from '../../../api/curator';
import { addIdsToRequestData } from '../../../components/admin/curator/CuratorHeader/useSaveProject';
import { ROOM_TYPE } from '../../../constants/roomConstants';
import {
  getCurrentUser,
  getCurrentUserId,
  getCurrentUserUserId,
} from '../../../contexts/UserContext';
import {
  deleteProjectBackup,
  getProjectAutoSave,
  updateProjectAutoSave,
} from '../../../helpers/idb';
import { dispatcher } from '../../../helpers/projectHelper';
import { isAdminOrOwner } from '../../../helpers/userHelper';
import { selectBackupData } from '../../../redux/slicers/admin/curatorBackupSlicer';
import {
  selectIndividualProjectData,
  selectRoomData,
  selectUuid,
} from '../../../redux/slicers/admin/curatorSlicer';
import { getAppState } from '../../../redux/store';
import { getRoomJsonFile } from './getRoomJsonFile';
import { isConfiguratorView, isRetailAutomation, isRetailConfigurator } from '../../../components/Configurator/utils';
import { saveSceneJson } from '../../../components/Configurator/utils/saveSceneJson';
import { saveHotspotJson } from '../../../components/RetailAutomation/common/saveHotspotJson';
import {
  isMattressBuilderPage,
  updateRoomJson,
} from '../../../components/admin/curator/ProductBuilderComponents/commonHelper';
import {
  ADMIN_DASHBOARD,
  ADMIN_MY_PRODUCTS_TEXTURE_EDIT_V2,
  COMMON_HOME,
} from '../../../constants/routePathConstants';

export let autosaveCounter = 0;
export let counterAfterSave = 0;

export function resetCounter() {
  counterAfterSave = 0;
}

export const saveCurrentProgress = debounce(
  async () => {
    // preventing autosave for Experience page where we are uploading models/textures in order to generate content
    const isExperiencePageWorking = [
      COMMON_HOME,
      ADMIN_DASHBOARD,
      ADMIN_MY_PRODUCTS_TEXTURE_EDIT_V2,
    ].includes(window.location.pathname);
    if (isExperiencePageWorking) return;

    if (isConfiguratorView()) {
      await saveSceneJson();
      return;
    }
    if (isRetailAutomation()) {
      await saveHotspotJson();
      return;
    }
    if (isMattressBuilderPage()) {
      await updateRoomJson({ delay: true, reason: 'save progress' });
      return;
    }
    autosaveCounter += 1;
    counterAfterSave += 1;
    const roomInfo = await getRoomJsonFile({ showError: false, rawOnly: true });

    if (roomInfo) {
      const projectData = {
        id: getUniqueCuratorUrl(),
        curatorUuid: selectUuid(getAppState()),
        data: roomInfo,
        timestamp: Date.now(),
      };

      await updateProjectAutoSave(projectData);

      if (autosaveCounter % 5 === 0) {
        updateBackendAutosave({ roomInfo });
      }
    }
  },
  350,
  { leading: false, trailing: true }
);

export const getAutoSaveData = async () => {
  const id = getUniqueCuratorUrl();
  const autoSaveData = await getProjectAutoSave(id);
  return autoSaveData;
};

export const getUniqueCuratorUrl = () => {
  const pathname = window.location.pathname;

  const params = new URLSearchParams(window.location.search);
  const zipUuid = params.get('zipUuid');
  const renderId = params.get('renderId');
  const automationId = params.get('automationId');
  const combinedWithConfiguratorId = params.get('combinedWithConfiguratorId');

  const newParams = new URLSearchParams('');

  if (zipUuid) {
    newParams.append('zipUuid', zipUuid);
  }

  if (renderId) {
    newParams.append('renderId', renderId);
  }

  if (automationId) {
    newParams.append('automationId', automationId);
  }

  if (combinedWithConfiguratorId) {
    newParams.append('combinedWithConfiguratorId', combinedWithConfiguratorId);
  }

  // const curatorUuid = selectUuid(getAppState()); // adds unique curator uuid so if project is opened in different tab id is still unique
  // newParams.append('curatorUuid', curatorUuid)

  return `${pathname}?${newParams}`;
};

export const deleteCurrentProjectBackup = async () => {
  const id = getUniqueCuratorUrl();
  try {
    await deleteProjectBackup(id);
  } catch (e) {
    //captureException(e);
    console.error('Unable to delete project backup');
    console.error(e);
  }
};

const updateBackendAutosave = async ({ roomInfo }) => {
  const projectData = selectIndividualProjectData(getAppState());
  const backupData = selectBackupData(getAppState());
  const backupCreated = Boolean(backupData);

    if (isRetailConfigurator()) {
       return null;
    }

  const currentUser = getCurrentUser();
  const canEditFile =
    projectData?.created_by === currentUser.id ||
    (isAdminOrOwner(currentUser) && projectData?.organization === currentUser.organization);

  const formData = new FormData();

  const json_data = JSON.stringify(roomInfo);
  const blob = new Blob([json_data], { type: 'application/json' });
  formData.append('file', blob, 'newproject.json');

  // name
  const name = projectData?.id ? `${projectData.name} autosave` : 'untitled autosave';
  formData.append('name', name);

  // project type
  const roomData = selectRoomData(getAppState());
  let projectType = projectData?.project_type || roomData?.room_type;
  if (!projectType) {
    //captureException('Project type is missing while creating backup. LF_LKJLJ@#JL$@J#L');
    projectType = ROOM_TYPE.LIFESTYLE;
  }

  formData.append('project_type', projectType);

  addIdsToRequestData({ data: formData, roomInfo });

  if (projectData.id && canEditFile) {
    // update backup
    await dispatcher(updateProjectBackup({ projectId: projectData.id, formData }));
    return;
  }

  if (backupCreated) {
    await dispatcher(updateProjectBackup({ projectId: backupData.id, formData }));
  } else {
    if (projectData?.thumbnail) {
      formData.append('thumbnail_url', projectData.thumbnail);
    }

    await dispatcher(createProjectBackup({ formData }));
  }
};
